/**
 * RepConnect - Unified Team Management Interface
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Mail, Phone, Shield,
  Edit2, CheckCircle, Clock, UserX,
  ChevronRight, AlertCircle, Crown, Key,
  UserPlus, Settings, Building2, Trash2, Printer
} from 'lucide-react';
import AdminDelegation from '../utils/adminDelegation';
import { staffService } from '../utils/supabaseStaffService';
import { logger } from '../utils/logger';
import emailService from '../utils/emailService';
import StaffPrintModal from './StaffPrintModal';
import PrintableStaffList from './PrintableStaffList';

// Permission constants
const PERMISSIONS = {
  VIEW_MEETINGS: 'view_meetings',
  MANAGE_MEETINGS: 'manage_meetings',
  MANAGE_STAFF: 'manage_staff',
  VIEW_REPORTS: 'view_reports',
  MANAGE_SETTINGS: 'manage_settings'
};

const TeamManagement = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('staff'); // 'staff' or 'admin'
  const [adminUsers, setAdminUsers] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [showEditStaffModal, setShowEditStaffModal] = useState(false);
  const [showDeleteStaffConfirm, setShowDeleteStaffConfirm] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);

  // Print staff directory state
  const [showStaffPrintModal, setShowStaffPrintModal] = useState(false);
  const [staffPrintData, setStaffPrintData] = useState(null);

  // Get the effective physician ID - for office_staff, use delegating_for
  const getEffectivePhysicianId = useCallback(() => {
    if (!currentUser?.id) return null;

    // If user is office_staff with delegating_for, use the physician's ID
    if (currentUser.user_type === 'office_staff' && currentUser.delegating_for) {
      logger.debug('Office staff using delegated physician ID for team management:', {
        staffId: currentUser.id,
        physicianId: currentUser.delegating_for
      });
      return currentUser.delegating_for;
    }

    return currentUser.id;
  }, [currentUser]);

  const loadAdminUsers = useCallback(async () => {
    const physicianId = getEffectivePhysicianId();
    if (!physicianId) {
      setAdminUsers([]);
      return;
    }

    try {
      logger.debug('Loading admin users from database for physician:', physicianId);
      const admins = await staffService.getAdminUsersForPhysician(physicianId);
      logger.debug('Loaded admin users from database:', admins.length);
      setAdminUsers(admins);
    } catch (error) {
      logger.error('Error loading admin users:', error);
      setAdminUsers([]);
    }
  }, [getEffectivePhysicianId]);

  const loadStaffMembers = useCallback(async () => {
    const physicianId = getEffectivePhysicianId();
    if (!physicianId) {
      setStaffMembers([]);
      return;
    }

    try {
      logger.debug('📋 Loading staff members from database for:', physicianId);

      // Check for localStorage data to migrate (use physician ID for consistency)
      const localStaff = localStorage.getItem(`staff_${physicianId}`);
      if (localStaff) {
        const localStaffData = JSON.parse(localStaff);
        logger.debug('🔄 Found local staff data, migrating to database:', localStaffData.length);

        // Migrate to Supabase
        await staffService.migrateLocalStaffData(physicianId, localStaffData);

        // Remove from localStorage after successful migration
        localStorage.removeItem(`staff_${physicianId}`);
        logger.debug('✅ Successfully migrated staff data to database');
      }

      // Load staff from database using physician ID
      const staff = await staffService.getStaffForPhysician(physicianId);
      logger.debug('✅ Loaded staff members from database:', staff.length);
      setStaffMembers(staff);
    } catch (error) {
      logger.error('❌ Error loading staff members:', error);
      setStaffMembers([]);
    }
  }, [getEffectivePhysicianId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        loadAdminUsers(),
        loadStaffMembers()
      ]);
      setLoading(false);
    };

    loadData();
  }, [loadAdminUsers, loadStaffMembers]);

  const handleInviteAdmin = async (adminData) => {
    const physicianId = getEffectivePhysicianId();
    if (!physicianId) {
      logger.error('No physician ID available');
      alert('Unable to create invitation. Please try again.');
      return;
    }

    try {
      logger.debug('Creating admin invitation for physician:', physicianId);

      // Create pending invitation in database
      const invitation = await staffService.createPendingAdminInvitation(
        physicianId,
        {
          email: adminData.email,
          role: adminData.role,
          name: adminData.name
        }
      );

      if (!invitation) {
        logger.error('Failed to create invitation');
        alert('Failed to create invitation. Please try again.');
        return;
      }

      // Send invitation email
      const inviteUrl = `${window.location.origin}/signup?inviteEmail=${encodeURIComponent(adminData.email)}`;

      const physicianName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() ||
                           currentUser.name ||
                           'the physician';

      const emailResult = await emailService.sendAdminInvite(
        adminData,
        physicianName,
        inviteUrl
      );

      if (emailResult.success) {
        logger.debug('✅ Invitation email sent successfully');
        alert(`Invitation sent to ${adminData.email}! They will receive an email with instructions to sign up.`);
      } else {
        logger.warn('⚠️ Invitation created but email failed to send', {
          error: emailResult.error,
          emailResult: emailResult
        });
        // Show detailed error in development, generic message in production
        const errorDetail = process.env.NODE_ENV === 'development'
          ? `\n\nError: ${emailResult.error}`
          : '';
        alert(`Invitation created for ${adminData.email}, but email delivery failed. Please contact them directly with the signup link.${errorDetail}`);
      }

      // Reload admin users to show the pending invitation
      await loadAdminUsers();
      setShowInviteModal(false);

    } catch (error) {
      logger.error('Error inviting admin:', error);
      alert('An error occurred while sending the invitation. Please try again.');
    }
  };

  const handleDeactivateAdmin = async (adminId) => {
    // Deactivate is essentially delete for now
    try {
      await staffService.deleteAdminUser(adminId);
      await loadAdminUsers();
    } catch (error) {
      logger.error('Error deactivating admin:', error);
    }
  };

  const handleDeleteAdmin = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAdmin = async () => {
    if (adminToDelete) {
      try {
        await staffService.deleteAdminUser(adminToDelete.id);
        await loadAdminUsers();
        setShowDeleteConfirm(false);
        setAdminToDelete(null);
      } catch (error) {
        logger.error('Error deleting admin:', error);
      }
    }
  };

  const handleEditAdmin = async (adminData) => {
    // TODO: Implement edit admin functionality
    logger.warn('Edit admin not yet implemented');
    await loadAdminUsers();
    setShowEditModal(false);
    setSelectedAdmin(null);
  };

  const handleAddStaff = async (staffData) => {
    const physicianId = getEffectivePhysicianId();
    if (!physicianId) {
      logger.error('No physician ID available');
      return;
    }

    try {
      logger.debug('➕ Adding staff member to database for physician:', physicianId);
      const newStaff = await staffService.addStaffMember(physicianId, staffData);

      if (newStaff) {
        logger.debug('✅ Successfully added staff member:', newStaff);
        setStaffMembers(prev => [...prev, newStaff]);
        setShowAddStaffModal(false);
      } else {
        logger.error('Failed to add staff member');
      }
    } catch (error) {
      logger.error('❌ Error adding staff member:', error);
    }
  };

  const handleEditStaff = async (staffData) => {
    if (!selectedStaff?.id) {
      logger.error('No staff selected for editing');
      return;
    }

    try {
      logger.debug('🔄 Updating staff member in database:', staffData);
      const updatedStaff = await staffService.updateStaffMember(selectedStaff.id, staffData);

      if (updatedStaff) {
        logger.debug('✅ Successfully updated staff member:', updatedStaff);
        setStaffMembers(prev =>
          prev.map(staff => staff.id === selectedStaff.id ? updatedStaff : staff)
        );
        setShowEditStaffModal(false);
        setSelectedStaff(null);
      } else {
        logger.error('Failed to update staff member');
      }
    } catch (error) {
      logger.error('❌ Error updating staff member:', error);
    }
  };

  const handleDeleteStaff = (staff) => {
    setStaffToDelete(staff);
    setShowDeleteStaffConfirm(true);
  };

  const confirmDeleteStaff = async () => {
    if (!staffToDelete?.id) return;

    try {
      logger.debug('🗑️ Deactivating staff member in database:', staffToDelete.id);
      const success = await staffService.deactivateStaffMember(staffToDelete.id);

      if (success) {
        logger.debug('✅ Successfully deactivated staff member');
        setStaffMembers(prev => prev.filter(staff => staff.id !== staffToDelete.id));
        setShowDeleteStaffConfirm(false);
        setStaffToDelete(null);
      } else {
        logger.error('Failed to deactivate staff member');
      }
    } catch (error) {
      logger.error('❌ Error deactivating staff member:', error);
    }
  };

  const handlePrintStaffDirectory = (selectedStaff) => {
    logger.debug('Printing staff directory', {
      totalStaff: staffMembers.length,
      selectedCount: selectedStaff.length
    });

    // Prepare provider info
    const providerInfo = {
      name: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Practice',
      company: currentUser?.company,
      npi: currentUser?.npi_number,
      location: currentUser?.location
    };

    setStaffPrintData({ staff: selectedStaff, providerInfo });
    setShowStaffPrintModal(false);

    // Wait for component to render, then trigger print
    setTimeout(() => {
      window.print();
      setStaffPrintData(null);
    }, 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'active': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
      case 'active':
        return CheckCircle;
      case 'pending': return Clock;
      case 'expired': return AlertCircle;
      default: return Clock;
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-600 mt-2">
            Manage your office staff and admin delegates
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('staff')}
            className={`px-6 py-3 rounded-md font-medium transition-colors ${
              activeTab === 'staff'
                ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Office Staff</span>
              <span className="bg-green-100 text-green-600 text-xs px-2 py-1 rounded-full">
                {staffMembers.length}
              </span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('admin')}
            className={`px-6 py-3 rounded-md font-medium transition-colors ${
              activeTab === 'admin'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4" />
              <span>Admin Delegates</span>
              <span className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full">
                {adminUsers.length}
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'staff' ? (
        <StaffSection
          staffMembers={staffMembers}
          onAddStaff={() => setShowAddStaffModal(true)}
          onEdit={(staff) => {
            setSelectedStaff(staff);
            setShowEditStaffModal(true);
          }}
          onDelete={handleDeleteStaff}
          onPrint={() => setShowStaffPrintModal(true)}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
        />
      ) : (
        <AdminSection
          adminUsers={adminUsers}
          onInvite={() => setShowInviteModal(true)}
          onEdit={(admin) => {
            setSelectedAdmin(admin);
            setShowEditModal(true);
          }}
          onDeactivate={handleDeactivateAdmin}
          onDelete={handleDeleteAdmin}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
        />
      )}

      {/* Modals */}
      {showInviteModal && (
        <InviteAdminModal
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteAdmin}
        />
      )}

      {showAddStaffModal && (
        <AddStaffModal
          onClose={() => setShowAddStaffModal(false)}
          onAdd={handleAddStaff}
        />
      )}

      {showEditModal && selectedAdmin && (
        <EditAdminModal
          admin={selectedAdmin}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAdmin(null);
          }}
          onSave={handleEditAdmin}
        />
      )}

      {showEditStaffModal && selectedStaff && (
        <EditStaffModal
          staff={selectedStaff}
          onClose={() => {
            setShowEditStaffModal(false);
            setSelectedStaff(null);
          }}
          onSave={handleEditStaff}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && adminToDelete && (
        <DeleteConfirmModal
          admin={adminToDelete}
          onClose={() => {
            setShowDeleteConfirm(false);
            setAdminToDelete(null);
          }}
          onConfirm={confirmDeleteAdmin}
        />
      )}

      {/* Staff Delete Confirmation Modal */}
      {showDeleteStaffConfirm && staffToDelete && (
        <DeleteStaffConfirmModal
          staff={staffToDelete}
          onClose={() => {
            setShowDeleteStaffConfirm(false);
            setStaffToDelete(null);
          }}
          onConfirm={confirmDeleteStaff}
        />
      )}

      {/* Staff Print Modal */}
      {showStaffPrintModal && (
        <StaffPrintModal
          isOpen={showStaffPrintModal}
          onClose={() => setShowStaffPrintModal(false)}
          staffMembers={staffMembers}
          onPrint={handlePrintStaffDirectory}
        />
      )}

      {/* Printable Staff List (hidden on screen, visible when printing) */}
      {staffPrintData && (
        <PrintableStaffList
          staffData={staffPrintData.staff}
          providerInfo={staffPrintData.providerInfo}
        />
      )}
    </div>
  );
};

const AdminSection = ({ adminUsers, onInvite, onEdit, onDeactivate, onDelete, getStatusColor, getStatusIcon }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Admin Delegates</h2>
        <p className="text-gray-600 text-sm">Grant portal access to office staff</p>
      </div>
      <button
        onClick={onInvite}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 hover:bg-blue-700"
      >
        <Plus className="w-5 h-5" />
        <span>Invite Admin</span>
      </button>
    </div>

    {adminUsers.length === 0 ? (
      <div className="bg-white rounded-xl shadow-md p-12 text-center">
        <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Admin Delegates</h3>
        <p className="text-gray-600 mb-6">
          Invite your office staff to help manage your portal
        </p>
        <button
          onClick={onInvite}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
        >
          Invite First Admin
        </button>
      </div>
    ) : (
      <div className="grid gap-4">
        {adminUsers.map(admin => {
          const StatusIcon = getStatusIcon(admin.inviteStatus);
          return (
            <div key={admin.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    {admin.inviteStatus === 'accepted' ? (
                      <Crown className="w-6 h-6 text-blue-600" />
                    ) : (
                      <Key className="w-6 h-6 text-blue-600" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{admin.name}</h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(admin.inviteStatus)} flex-shrink-0`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {admin.inviteStatus}
                      </span>
                    </div>

                    <p className="text-blue-600 font-medium mb-2 text-sm sm:text-base">{admin.title}</p>

                    <div className="flex flex-col sm:flex-row sm:items-center text-gray-600 text-sm gap-2 sm:gap-4">
                      <div className="flex items-center min-w-0">
                        <Mail className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">{admin.email}</span>
                      </div>
                      {admin.phone && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-1 flex-shrink-0" />
                          <span>{admin.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 sm:flex-shrink-0">
                  <button
                    onClick={() => onEdit(admin)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Edit admin details"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>

                  {admin.isActive && (
                    <button
                      onClick={() => onDeactivate(admin.id)}
                      className="p-2 text-orange-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                      title="Deactivate admin"
                    >
                      <UserX className="w-4 h-4" />
                    </button>
                  )}

                  <button
                    onClick={() => onDelete(admin)}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete admin permanently"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-600">
                    <Shield className="w-4 h-4 mr-1" />
                    <span>{admin.permissions?.length || 0} permissions granted</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const StaffSection = ({ staffMembers, onAddStaff, onEdit, onDelete, onPrint, getStatusColor, getStatusIcon }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center space-x-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Office Staff</h2>
          <p className="text-gray-600 text-sm">Manage your clinic team members</p>
        </div>
        {staffMembers.length > 0 && (
          <button
            onClick={onPrint}
            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
            title="Print staff directory"
          >
            <Printer className="w-5 h-5" />
          </button>
        )}
      </div>
      <button
        onClick={onAddStaff}
        className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 hover:bg-green-700"
      >
        <UserPlus className="w-5 h-5" />
        <span>Add Staff</span>
      </button>
    </div>

    {staffMembers.length === 0 ? (
      <div className="bg-white rounded-xl shadow-md p-12 text-center">
        <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Staff Members</h3>
        <p className="text-gray-600 mb-6">
          Add your office staff to keep track of your team
        </p>
        <button
          onClick={onAddStaff}
          className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700"
        >
          Add First Staff Member
        </button>
      </div>
    ) : (
      <div className="grid gap-4">
        {staffMembers.map(staff => {
          const StatusIcon = getStatusIcon(staff.status);
          return (
            <div key={staff.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{staff.name}</h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(staff.status)} flex-shrink-0`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {staff.status}
                      </span>
                    </div>

                    <p className="text-green-600 font-medium mb-2 text-sm sm:text-base">{staff.role}</p>

                    {(staff.email || staff.phone) && (
                      <div className="flex flex-col sm:flex-row sm:items-center text-gray-600 text-sm gap-2 sm:gap-4 mb-2">
                        {staff.email && (
                          <div className="flex items-center min-w-0">
                            <Mail className="w-4 h-4 mr-1 flex-shrink-0" />
                            <span className="truncate">{staff.email}</span>
                          </div>
                        )}
                        {staff.phone && (
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-1 flex-shrink-0" />
                            <span>{staff.phone}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {staff.department && (
                      <div className="flex items-center text-gray-500 text-sm">
                        <Building2 className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">{staff.department}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2 sm:flex-shrink-0">
                  <button
                    onClick={() => onEdit(staff)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Edit staff member"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => onDelete(staff)}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete staff member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// Reuse existing modals from AdminManagement
const InviteAdminModal = ({ onClose, onInvite }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'scheduler',
    title: '',
    permissions: []
  });

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'office_manager': 'Office Manager',
      'scheduler': 'Scheduler',
      'admin': 'Administrator',
      'viewer': 'Viewer'
    };
    return roleNames[role] || role;
  };

  const getDefaultPermissions = (role) => {
    // Default permissions based on role
    const permissions = {
      'office_manager': ['view_meetings', 'manage_meetings', 'manage_staff', 'view_reports'],
      'scheduler': ['view_meetings', 'manage_meetings'],
      'admin': ['view_meetings', 'manage_meetings', 'manage_staff', 'view_reports', 'manage_settings'],
      'viewer': ['view_meetings']
    };
    return permissions[role] || [];
  };

  const handleRoleChange = (role) => {
    const defaultPermissions = getDefaultPermissions(role);
    setFormData(prev => ({
      ...prev,
      role,
      title: getRoleDisplayName(role),
      permissions: defaultPermissions
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onInvite(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Invite Admin User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={formData.role}
              onChange={(e) => handleRoleChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="scheduler">Scheduler</option>
              <option value="lead_medical_assistant">Lead Medical Assistant</option>
              <option value="practice_coordinator">Practice Coordinator</option>
              <option value="office_manager">Office Manager</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Send Invite
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddStaffModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    department: 'Clinical'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Add Staff Member</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="staff@practice.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="(555) 123-4567"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              placeholder="e.g. Medical Assistant, Nurse, Receptionist"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <select
              value={formData.department}
              onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="Clinical">Clinical</option>
              <option value="Administrative">Administrative</option>
              <option value="Nursing">Nursing</option>
              <option value="Support">Support</option>
            </select>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
            >
              Add Staff
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditAdminModal = ({ admin, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: admin.name,
    email: admin.email,
    phone: admin.phone || '',
    role: admin.role,
    title: admin.title,
    isActive: admin.isActive
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Edit Admin User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="mr-2"
              />
              <span className="text-sm font-medium">Active User</span>
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditStaffModal = ({ staff, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: staff.name,
    email: staff.email || '',
    phone: staff.phone || '',
    role: staff.role,
    department: staff.department,
    status: staff.status
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Edit Staff Member</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="staff@practice.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="(555) 123-4567"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              placeholder="e.g. Medical Assistant, Nurse, Receptionist"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <select
              value={formData.department}
              onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="Clinical">Clinical</option>
              <option value="Administrative">Administrative</option>
              <option value="Nursing">Nursing</option>
              <option value="Support">Support</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="on_leave">On Leave</option>
            </select>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ admin, onClose, onConfirm }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-xl max-w-md w-full p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-red-600">Delete Admin Delegate</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          ×
        </button>
      </div>

      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{admin.name}</h3>
            <p className="text-sm text-gray-600">{admin.title}</p>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-800 font-medium mb-1">
                This action cannot be undone
              </p>
              <p className="text-sm text-red-700">
                Deleting this admin will permanently remove their access and all associated data. 
                They will no longer be able to manage your portal.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
        >
          Delete Admin
        </button>
      </div>
    </div>
  </div>
);

const DeleteStaffConfirmModal = ({ staff, onClose, onConfirm }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-xl max-w-md w-full p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-red-600">Delete Staff Member</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          ×
        </button>
      </div>

      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{staff.name}</h3>
            <p className="text-sm text-gray-600">{staff.role}</p>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-800 font-medium mb-1">
                This action cannot be undone
              </p>
              <p className="text-sm text-red-700">
                Deleting this staff member will permanently remove them from your team records.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
        >
          Delete Staff Member
        </button>
      </div>
    </div>
  </div>
);

export default React.memo(TeamManagement);